import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import {
    Idea,
    Timeline,
    PlotPoint,
    CharacterArc,
    Fragment,
    IdeaCard,
    CardInstance,
    AttachedEntity,
    CardRelationship,
    Dynamic,
    DynamicType,
    IdeaWorkspaceState,
    WorkspaceView,
    TimelineMode,
    FocusLock,
    EntityType,
    CardRelationType,
    IdeaBoardItem,
    IdeaBeatLink
} from '../types';
import { IdeaService, BoardGeometry } from '../lib/ideaService';
import type { StructureTemplate } from '../lib/structureTemplates';

interface IdeaContextType {
    // Current idea data
    currentIdea: Idea | null;
    timelines: Timeline[];
    characterArcs: CharacterArc[];
    fragments: Fragment[];

    // Card system data
    cards: IdeaCard[];
    cardRelationships: CardRelationship[];

    // Beat Board data (free-canvas geometry + connectors)
    boardItems: IdeaBoardItem[];
    beatLinks: IdeaBeatLink[];

    // Computed dynamics (not persisted)
    dynamics: Dynamic[];

    // Loading states
    isLoading: boolean;
    isSaving: boolean;

    // Workspace state
    workspaceState: IdeaWorkspaceState;

    // Actions
    setCurrentIdea: (idea: Idea | null) => void;
    loadIdea: (ideaId: string) => Promise<void>;
    loadIdeaByProjectId: (projectId: string) => Promise<Idea | null>;
    createIdea: (title: string, projectId?: string) => Promise<Idea | null>;
    updateIdea: (updates: Partial<Idea>) => Promise<void>;
    refreshIdea: () => Promise<void>;

    // Workspace state updates
    updateWorkspaceState: (updates: Partial<IdeaWorkspaceState>) => void;
    setView: (view: WorkspaceView) => void;
    setTimelineMode: (mode: TimelineMode) => void;
    setFocusLock: (lock: FocusLock) => void;
    setNeedlePosition: (position: number) => void;
    setTimelineZoom: (zoom: number) => void;
    selectCard: (cardId: string | null) => void;
    selectInstance: (instanceId: string | null) => void;
    selectDynamic: (dynamic: Dynamic | null) => void;

    // Card actions
    createCard: (data?: { title?: string; content?: string }) => Promise<IdeaCard | null>;
    updateCard: (cardId: string, updates: Partial<IdeaCard>) => Promise<void>;
    deleteCard: (cardId: string) => Promise<void>;

    // Card instance actions (timeline placement)
    placeCard: (cardId: string, lane: 'macro' | 'micro', orderIndex?: number) => Promise<CardInstance | null>;
    updateInstance: (instanceId: string, updates: Partial<CardInstance>) => Promise<void>;
    removeInstance: (instanceId: string) => Promise<void>;
    unplaceCard: (cardId: string) => Promise<void>;
    reorderInstances: (lane: 'macro' | 'micro', orderedInstanceIds: string[]) => Promise<void>;

    // Entity attachment actions
    attachEntity: (cardId: string, entityType: EntityType, refId: string) => Promise<AttachedEntity | null>;
    detachEntity: (entityId: string, cardId: string) => Promise<void>;

    // Card relationship actions
    createRelationship: (fromCardId: string, toCardId: string, type: CardRelationType) => Promise<CardRelationship | null>;
    deleteRelationship: (relationshipId: string) => Promise<void>;

    // Beat Board actions
    saveBoardItem: (cardId: string, geometry: BoardGeometry) => Promise<void>;
    applyStructureTemplate: (template: StructureTemplate) => Promise<void>;
    createBeatLink: (fromCardId: string, toCardId: string) => Promise<IdeaBeatLink | null>;
    deleteBeatLink: (linkId: string) => Promise<void>;

    // Legacy timeline actions (for backwards compatibility)
    createTimeline: (type: 'macro' | 'mini' | 'spine', title: string) => Promise<Timeline | null>;
    createPlotPoint: (timelineId: string, title: string, startPos: number, endPos: number) => Promise<PlotPoint | null>;
    updatePlotPoint: (id: string, updates: Partial<PlotPoint>) => Promise<void>;
    deletePlotPoint: (id: string) => Promise<void>;

    // Character arc actions
    castCharacter: (characterId: string, startPos?: number, endPos?: number) => Promise<CharacterArc | null>;
    updateCharacterArc: (id: string, updates: Partial<CharacterArc>) => Promise<void>;
    removeCharacter: (id: string) => Promise<void>;

    // Fragment actions
    createFragment: (title: string, type?: 'memory' | 'flashback' | 'note' | 'foreshadow' | 'subplot') => Promise<Fragment | null>;
    updateFragment: (id: string, updates: Partial<Fragment>) => Promise<void>;
    deleteFragment: (id: string) => Promise<void>;
}

const defaultWorkspaceState: IdeaWorkspaceState = {
    view: 'card',
    selectedCardId: null,
    selectedInstanceId: null,
    selectedDynamic: null,
    timelineMode: 'add',
    focusLock: 'both',
    needlePosition: 0,
    timelineZoom: 1,
    timelinePan: 0,
    isAnchorModalOpen: false,
};

const IdeaContext = createContext<IdeaContextType | undefined>(undefined);

export const IdeaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentIdea, setCurrentIdea] = useState<Idea | null>(null);
    const [timelines, setTimelines] = useState<Timeline[]>([]);
    const [characterArcs, setCharacterArcs] = useState<CharacterArc[]>([]);
    const [fragments, setFragments] = useState<Fragment[]>([]);
    const [cards, setCards] = useState<IdeaCard[]>([]);
    const [cardRelationships, setCardRelationships] = useState<CardRelationship[]>([]);
    const [boardItems, setBoardItems] = useState<IdeaBoardItem[]>([]);
    const [beatLinks, setBeatLinks] = useState<IdeaBeatLink[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [workspaceState, setWorkspaceState] = useState<IdeaWorkspaceState>(defaultWorkspaceState);

    // Compute dynamics from cards (not persisted)
    const dynamics = useMemo(() => {
        const dynamicMap = new Map<string, Dynamic>();

        cards.forEach(card => {
            card.entities.forEach(entity => {
                // Character dynamics
                if (entity.type === 'subject') {
                    const key = entity.ref_id;
                    if (!dynamicMap.has(`char-${key}`)) {
                        dynamicMap.set(`char-${key}`, {
                            type: 'character' as DynamicType,
                            key,
                            label: entity.name,
                            entity_ids: [entity.ref_id],
                            instanceCount: 0
                        });
                    }
                    const d = dynamicMap.get(`char-${key}`)!;
                    d.instanceCount += (card.instances?.length || 0);
                }

                // Location dynamics
                if (entity.type === 'location') {
                    const key = entity.ref_id;
                    if (!dynamicMap.has(`loc-${key}`)) {
                        dynamicMap.set(`loc-${key}`, {
                            type: 'location' as DynamicType,
                            key,
                            label: entity.name,
                            entity_ids: [entity.ref_id],
                            instanceCount: 0
                        });
                    }
                    const d = dynamicMap.get(`loc-${key}`)!;
                    d.instanceCount += (card.instances?.length || 0);
                }

                // Prop dynamics
                if (entity.type === 'object') {
                    const key = entity.ref_id;
                    if (!dynamicMap.has(`prop-${key}`)) {
                        dynamicMap.set(`prop-${key}`, {
                            type: 'prop' as DynamicType,
                            key,
                            label: entity.name,
                            entity_ids: [entity.ref_id],
                            instanceCount: 0
                        });
                    }
                    const d = dynamicMap.get(`prop-${key}`)!;
                    d.instanceCount += (card.instances?.length || 0);
                }
            });

            // Relationship dynamics (cards with 2+ subjects)
            const subjects = card.entities.filter(e => e.type === 'subject');
            if (subjects.length >= 2) {
                // Create pairwise relationships
                for (let i = 0; i < subjects.length; i++) {
                    for (let j = i + 1; j < subjects.length; j++) {
                        const ids = [subjects[i].ref_id, subjects[j].ref_id].sort();
                        const key = ids.join('-');
                        if (!dynamicMap.has(`rel-${key}`)) {
                            dynamicMap.set(`rel-${key}`, {
                                type: 'relationship' as DynamicType,
                                key,
                                label: `${subjects[i].name} ↔ ${subjects[j].name}`,
                                entity_ids: ids,
                                instanceCount: 0
                            });
                        }
                        const d = dynamicMap.get(`rel-${key}`)!;
                        d.instanceCount += (card.instances?.length || 0);
                    }
                }
            }
        });

        return Array.from(dynamicMap.values()).filter(d => d.instanceCount > 0);
    }, [cards]);

    // Load full idea with relations + cards
    const loadIdea = useCallback(async (ideaId: string) => {
        setIsLoading(true);
        try {
            const idea = await IdeaService.getIdeaWithRelations(ideaId);
            if (idea) {
                setCurrentIdea(idea);
                setTimelines(idea.timelines || []);
                setCharacterArcs(idea.character_arcs || []);
                setFragments(idea.fragments || []);

                // Load cards
                const loadedCards = await IdeaService.getCards(ideaId);
                setCards(loadedCards);

                // Load card relationships
                const loadedRelationships = await IdeaService.getCardRelationships(ideaId);
                setCardRelationships(loadedRelationships);

                // Load Beat Board geometry and connectors
                const [loadedBoardItems, loadedBeatLinks] = await Promise.all([
                    IdeaService.getBoardItems(ideaId),
                    IdeaService.getBeatLinks(ideaId)
                ]);
                setBoardItems(loadedBoardItems);
                setBeatLinks(loadedBeatLinks);
            }
        } catch (error) {
            console.error('Failed to load idea:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load idea by project ID
    const loadIdeaByProjectId = useCallback(async (projectId: string): Promise<Idea | null> => {
        setIsLoading(true);
        try {
            const idea = await IdeaService.getIdeaByProjectId(projectId);
            if (idea) {
                await loadIdea(idea.id);
                return idea;
            }
            return null;
        } catch (error) {
            console.error('Failed to load idea by project:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [loadIdea]);

    // Create new idea
    const createIdea = useCallback(async (title: string, projectId?: string): Promise<Idea | null> => {
        setIsSaving(true);
        try {
            const idea = await IdeaService.createIdea(title, projectId);
            if (idea) {
                await loadIdea(idea.id);
                return idea;
            }
            return null;
        } catch (error) {
            console.error('Failed to create idea:', error);
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [loadIdea]);

    // Update current idea
    const updateIdea = useCallback(async (updates: Partial<Idea>) => {
        if (!currentIdea) return;
        setIsSaving(true);
        try {
            await IdeaService.updateIdea(currentIdea.id, updates);
            setCurrentIdea(prev => prev ? { ...prev, ...updates } : null);
        } catch (error) {
            console.error('Failed to update idea:', error);
        } finally {
            setIsSaving(false);
        }
    }, [currentIdea]);

    // Refresh current idea
    const refreshIdea = useCallback(async () => {
        if (currentIdea?.id) {
            await loadIdea(currentIdea.id);
        }
    }, [currentIdea?.id, loadIdea]);

    // Workspace state updates
    const updateWorkspaceState = useCallback((updates: Partial<IdeaWorkspaceState>) => {
        setWorkspaceState(prev => ({ ...prev, ...updates }));
    }, []);

    const setView = useCallback((view: WorkspaceView) => {
        setWorkspaceState(prev => ({ ...prev, view }));
    }, []);

    const setTimelineMode = useCallback((mode: TimelineMode) => {
        setWorkspaceState(prev => ({ ...prev, timelineMode: mode }));
    }, []);

    const setFocusLock = useCallback((lock: FocusLock) => {
        setWorkspaceState(prev => ({ ...prev, focusLock: lock }));
    }, []);

    const setNeedlePosition = useCallback((position: number) => {
        setWorkspaceState(prev => ({ ...prev, needlePosition: Math.max(0, Math.min(1, position)) }));
    }, []);

    const setTimelineZoom = useCallback((zoom: number) => {
        setWorkspaceState(prev => ({ ...prev, timelineZoom: Math.max(0.25, Math.min(4, zoom)) }));
    }, []);

    const selectCard = useCallback((cardId: string | null) => {
        setWorkspaceState(prev => ({
            ...prev,
            selectedCardId: cardId,
            selectedInstanceId: null
        }));
    }, []);

    const selectInstance = useCallback((instanceId: string | null) => {
        // When selecting an instance, also select its card
        if (instanceId) {
            // Find the card that owns this instance
            const cardWithInstance = cards.find(c =>
                c.instances?.some(i => i.id === instanceId)
            );
            setWorkspaceState(prev => ({
                ...prev,
                selectedCardId: cardWithInstance?.id || null,
                selectedInstanceId: instanceId
            }));
        } else {
            setWorkspaceState(prev => ({
                ...prev,
                selectedInstanceId: null
            }));
        }
    }, [cards]);

    const selectDynamic = useCallback((dynamic: Dynamic | null) => {
        setWorkspaceState(prev => ({
            ...prev,
            selectedDynamic: dynamic,
            view: dynamic ? 'dynamic' : prev.view
        }));
    }, []);

    // Card actions
    const createCard = useCallback(async (data?: { title?: string; content?: string }) => {
        if (!currentIdea) return null;
        setIsSaving(true);
        try {
            const card = await IdeaService.createCard(currentIdea.id, data);
            if (card) {
                setCards(prev => [...prev, card]);
                selectCard(card.id);
            }
            return card;
        } catch (error) {
            console.error('Failed to create card:', error);
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [currentIdea, selectCard]);

    const updateCard = useCallback(async (cardId: string, updates: Partial<IdeaCard>) => {
        setIsSaving(true);
        try {
            await IdeaService.updateCard(cardId, updates);
            setCards(prev => prev.map(c => c.id === cardId ? { ...c, ...updates } : c));
        } catch (error) {
            console.error('Failed to update card:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    const deleteCard = useCallback(async (cardId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.deleteCard(cardId);
            setCards(prev => prev.filter(c => c.id !== cardId));
            // Board geometry and connectors cascade in the database
            setBoardItems(prev => prev.filter(item => item.card_id !== cardId));
            setBeatLinks(prev => prev.filter(link => link.from_card_id !== cardId && link.to_card_id !== cardId));
            if (workspaceState.selectedCardId === cardId) {
                selectCard(null);
            }
        } catch (error) {
            console.error('Failed to delete card:', error);
        } finally {
            setIsSaving(false);
        }
    }, [workspaceState.selectedCardId, selectCard]);

    // Card instance actions
    const placeCard = useCallback(async (cardId: string, lane: 'macro' | 'micro', orderIndex?: number) => {
        setIsSaving(true);
        try {
            const instance = await IdeaService.placeCardOnTimeline(cardId, lane, orderIndex);
            if (instance) {
                // Refresh the card to get updated instances
                const updatedCard = await IdeaService.getCard(cardId);
                if (updatedCard) {
                    setCards(prev => prev.map(c => c.id === cardId ? updatedCard : c));
                }
            }
            return instance;
        } catch (error) {
            console.error('Failed to place card:', error);
            return null;
        } finally {
            setIsSaving(false);
        }
    }, []);

    const updateInstance = useCallback(async (instanceId: string, updates: Partial<CardInstance>) => {
        setIsSaving(true);
        try {
            await IdeaService.updateInstance(instanceId, updates);
            // Update the instance in its card
            setCards(prev => prev.map(card => ({
                ...card,
                instances: card.instances?.map(i =>
                    i.id === instanceId ? { ...i, ...updates } : i
                )
            })));
        } catch (error) {
            console.error('Failed to update instance:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    const removeInstance = useCallback(async (instanceId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.deleteInstance(instanceId);
            // Remove the instance from its card
            setCards(prev => prev.map(card => ({
                ...card,
                instances: card.instances?.filter(i => i.id !== instanceId)
            })));
            if (workspaceState.selectedInstanceId === instanceId) {
                selectInstance(null);
            }
        } catch (error) {
            console.error('Failed to remove instance:', error);
        } finally {
            setIsSaving(false);
        }
    }, [workspaceState.selectedInstanceId, selectInstance]);

    const unplaceCard = useCallback(async (cardId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.removeCardFromTimeline(cardId);
            // Clear instances from the card
            setCards(prev => prev.map(c =>
                c.id === cardId ? { ...c, instances: [] } : c
            ));
        } catch (error) {
            console.error('Failed to unplace card:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    // Reorder instances within a lane
    const reorderInstances = useCallback(async (lane: 'macro' | 'micro', orderedInstanceIds: string[]) => {
        setIsSaving(true);
        try {
            // Build the order updates
            const instanceOrders = orderedInstanceIds.map((id, index) => ({
                id,
                order_index: index
            }));

            // Update in database
            await IdeaService.reorderInstances(instanceOrders);

            // Update local state optimistically
            setCards(prev => prev.map(card => ({
                ...card,
                instances: card.instances?.map(instance => {
                    const newIndex = orderedInstanceIds.indexOf(instance.id);
                    if (newIndex !== -1 && instance.lane === lane) {
                        return { ...instance, order_index: newIndex };
                    }
                    return instance;
                })
            })));
        } catch (error) {
            console.error('Failed to reorder instances:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    // Entity attachment actions
    const attachEntity = useCallback(async (cardId: string, entityType: EntityType, refId: string) => {
        setIsSaving(true);
        try {
            const entity = await IdeaService.addEntityToCard(cardId, entityType, refId);
            if (entity) {
                // Refresh the card to get updated entities and potentially new focus
                const updatedCard = await IdeaService.getCard(cardId);
                if (updatedCard) {
                    // Auto-update focus if not locked
                    if (!updatedCard.focus_locked) {
                        const newFocus = IdeaService.inferCardFocus(updatedCard.entities);
                        if (newFocus !== updatedCard.focus) {
                            await IdeaService.updateCard(cardId, { focus: newFocus });
                            updatedCard.focus = newFocus;
                        }
                    }
                    setCards(prev => prev.map(c => c.id === cardId ? updatedCard : c));
                }
            }
            return entity;
        } catch (error) {
            console.error('Failed to attach entity:', error);
            return null;
        } finally {
            setIsSaving(false);
        }
    }, []);

    const detachEntity = useCallback(async (entityId: string, cardId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.removeEntityFromCard(entityId);
            // Refresh the card to get updated entities and potentially new focus
            const updatedCard = await IdeaService.getCard(cardId);
            if (updatedCard) {
                // Auto-update focus if not locked
                if (!updatedCard.focus_locked) {
                    const newFocus = IdeaService.inferCardFocus(updatedCard.entities);
                    if (newFocus !== updatedCard.focus) {
                        await IdeaService.updateCard(cardId, { focus: newFocus });
                        updatedCard.focus = newFocus;
                    }
                }
                setCards(prev => prev.map(c => c.id === cardId ? updatedCard : c));
            }
        } catch (error) {
            console.error('Failed to detach entity:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    // Card relationship actions
    const createRelationship = useCallback(async (fromCardId: string, toCardId: string, type: CardRelationType) => {
        if (!currentIdea) return null;
        setIsSaving(true);
        try {
            const relationship = await IdeaService.createCardRelationship(currentIdea.id, fromCardId, toCardId, type);
            if (relationship) {
                setCardRelationships(prev => [...prev, relationship]);
            }
            return relationship;
        } catch (error) {
            console.error('Failed to create relationship:', error);
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [currentIdea]);

    const deleteRelationship = useCallback(async (relationshipId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.deleteCardRelationship(relationshipId);
            setCardRelationships(prev => prev.filter(r => r.id !== relationshipId));
        } catch (error) {
            console.error('Failed to delete relationship:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    // Beat Board actions
    const saveBoardItem = useCallback(async (cardId: string, geometry: BoardGeometry) => {
        if (!currentIdea) return;
        const ideaId = currentIdea.id;
        // Optimistic: move the card immediately, reconcile with the saved row
        setBoardItems(prev => {
            const existing = prev.find(item => item.card_id === cardId);
            if (existing) return prev.map(item => item.card_id === cardId ? { ...item, ...geometry } : item);
            const now = new Date().toISOString();
            return [...prev, {
                id: `pending-${cardId}`, idea_id: ideaId, card_id: cardId, board: 'beat',
                z_index: 0, created_at: now, updated_at: now, ...geometry
            }];
        });
        setIsSaving(true);
        try {
            const saved = await IdeaService.saveBoardItem(ideaId, cardId, geometry);
            if (saved) setBoardItems(prev => prev.map(item => item.card_id === cardId ? saved : item));
        } catch (error) {
            console.error('Failed to save board item:', error);
            setBoardItems(await IdeaService.getBoardItems(ideaId));
        } finally {
            setIsSaving(false);
        }
    }, [currentIdea]);

    const applyStructureTemplate = useCallback(async (template: StructureTemplate) => {
        if (!currentIdea) return;
        const ideaId = currentIdea.id;
        const hasPlacements = cards.some(c => (c.instances?.length ?? 0) > 0);
        // An empty outline adopts the template's suggested length; otherwise keep the writer's
        const targetPages = hasPlacements ? (currentIdea.outline_target_pages ?? 120) : template.targetPages;
        const boardTop = boardItems.reduce((max, item) => Math.max(max, item.y + item.height), 0) + (boardItems.length ? 80 : 40);
        setIsSaving(true);
        try {
            if (!hasPlacements && currentIdea.outline_target_pages !== targetPages) {
                await IdeaService.updateIdea(ideaId, { outline_target_pages: targetPages });
                setCurrentIdea(prev => prev ? { ...prev, outline_target_pages: targetPages } : prev);
            }
            await IdeaService.applyStructureTemplate(ideaId, template, targetPages, boardTop);
        } catch (error) {
            console.error('Failed to apply structure template:', error);
        } finally {
            // Reload what the template touched (partial failures included)
            const [loadedCards, loadedBoardItems] = await Promise.all([
                IdeaService.getCards(ideaId),
                IdeaService.getBoardItems(ideaId)
            ]);
            setCards(loadedCards);
            setBoardItems(loadedBoardItems);
            setIsSaving(false);
        }
    }, [currentIdea, cards, boardItems]);

    const createBeatLink = useCallback(async (fromCardId: string, toCardId: string) => {
        if (!currentIdea) return null;
        setIsSaving(true);
        try {
            const link = await IdeaService.createBeatLink(currentIdea.id, fromCardId, toCardId);
            if (link) setBeatLinks(prev => [...prev, link]);
            return link;
        } finally {
            setIsSaving(false);
        }
    }, [currentIdea]);

    const deleteBeatLink = useCallback(async (linkId: string) => {
        setIsSaving(true);
        try {
            await IdeaService.deleteBeatLink(linkId);
            setBeatLinks(prev => prev.filter(link => link.id !== linkId));
        } catch (error) {
            console.error('Failed to delete beat link:', error);
        } finally {
            setIsSaving(false);
        }
    }, []);

    // Legacy timeline actions
    const createTimeline = useCallback(async (type: 'macro' | 'mini' | 'spine', title: string) => {
        if (!currentIdea) return null;
        const timeline = await IdeaService.createTimeline(currentIdea.id, type, title);
        if (timeline) {
            setTimelines(prev => [...prev, timeline]);
        }
        return timeline;
    }, [currentIdea]);

    const createPlotPoint = useCallback(async (timelineId: string, title: string, startPos: number, endPos: number) => {
        const plotPoint = await IdeaService.createPlotPoint(timelineId, title, startPos, endPos);
        if (plotPoint) {
            setTimelines(prev => prev.map(t =>
                t.id === timelineId
                    ? { ...t, plot_points: [...(t.plot_points || []), plotPoint] }
                    : t
            ));
        }
        return plotPoint;
    }, []);

    const updatePlotPoint = useCallback(async (id: string, updates: Partial<PlotPoint>) => {
        await IdeaService.updatePlotPoint(id, updates);
        setTimelines(prev => prev.map(t => ({
            ...t,
            plot_points: t.plot_points?.map(pp => pp.id === id ? { ...pp, ...updates } : pp)
        })));
    }, []);

    const deletePlotPoint = useCallback(async (id: string) => {
        await IdeaService.deletePlotPoint(id);
        setTimelines(prev => prev.map(t => ({
            ...t,
            plot_points: t.plot_points?.filter(pp => pp.id !== id)
        })));
    }, []);

    // Character arc actions
    const castCharacter = useCallback(async (characterId: string, startPos = 0, endPos = 1) => {
        if (!currentIdea) return null;
        const arc = await IdeaService.castCharacter(currentIdea.id, characterId, startPos, endPos);
        if (arc) {
            setCharacterArcs(prev => [...prev, arc]);
        }
        return arc;
    }, [currentIdea]);

    const updateCharacterArc = useCallback(async (id: string, updates: Partial<CharacterArc>) => {
        await IdeaService.updateCharacterArc(id, updates);
        setCharacterArcs(prev => prev.map(arc => arc.id === id ? { ...arc, ...updates } : arc));
    }, []);

    const removeCharacter = useCallback(async (id: string) => {
        await IdeaService.removeCharacterFromIdea(id);
        setCharacterArcs(prev => prev.filter(arc => arc.id !== id));
    }, []);

    // Fragment actions
    const createFragment = useCallback(async (title: string, type: 'memory' | 'flashback' | 'note' | 'foreshadow' | 'subplot' = 'note') => {
        if (!currentIdea) return null;
        const fragment = await IdeaService.createFragment(currentIdea.id, title, type);
        if (fragment) {
            setFragments(prev => [...prev, fragment]);
        }
        return fragment;
    }, [currentIdea]);

    const updateFragment = useCallback(async (id: string, updates: Partial<Fragment>) => {
        await IdeaService.updateFragment(id, updates);
        setFragments(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
    }, []);

    const deleteFragment = useCallback(async (id: string) => {
        await IdeaService.deleteFragment(id);
        setFragments(prev => prev.filter(f => f.id !== id));
    }, []);

    const value: IdeaContextType = {
        currentIdea,
        timelines,
        characterArcs,
        fragments,
        cards,
        cardRelationships,
        boardItems,
        beatLinks,
        dynamics,
        isLoading,
        isSaving,
        workspaceState,
        setCurrentIdea,
        loadIdea,
        loadIdeaByProjectId,
        createIdea,
        updateIdea,
        refreshIdea,
        updateWorkspaceState,
        setView,
        setTimelineMode,
        setFocusLock,
        setNeedlePosition,
        setTimelineZoom,
        selectCard,
        selectInstance,
        selectDynamic,
        createCard,
        updateCard,
        deleteCard,
        placeCard,
        updateInstance,
        removeInstance,
        unplaceCard,
        reorderInstances,
        attachEntity,
        detachEntity,
        createRelationship,
        deleteRelationship,
        saveBoardItem,
        applyStructureTemplate,
        createBeatLink,
        deleteBeatLink,
        createTimeline,
        createPlotPoint,
        updatePlotPoint,
        deletePlotPoint,
        castCharacter,
        updateCharacterArc,
        removeCharacter,
        createFragment,
        updateFragment,
        deleteFragment,
    };

    return (
        <IdeaContext.Provider value={value}>
            {children}
        </IdeaContext.Provider>
    );
};

export const useIdea = () => {
    const context = useContext(IdeaContext);
    if (context === undefined) {
        throw new Error('useIdea must be used within an IdeaProvider');
    }
    return context;
};
